import { ReactElement, useState } from 'react';
import sizeMe, { SizeMeProps } from 'react-sizeme';

import gtmIds, { dataId } from 'adapter/in/dashboards/config/gtm';
import { StyledGrabbale } from 'adapter/in/dashboards/pages/Widget/Wrapper/style';
import WidgetActionsMenuContainer from 'adapter/in/common/containers/Widget/WidgetActionsMenu';
import { WidgetType } from 'domain/entity/widget';
import Branding from 'domain/entity/branding';
import { ConstraintsPerLayoutElement, LayoutType } from 'domain/entity/layout';
import { KeyAndNamePair } from 'adapter/in/common/context/currentWidgetConfiguration';

import SmallAlertTooltip from 'adapter/in/dashboards/components/SmallAlertTooltip';
import { AnalysisActionsMenuContainer } from 'adapter/in/common/containers/Widget/AnalysisActionsMenu/AnalysisActionsMenu';
import { GridOrigin } from 'adapter/in/dashboards/containers/Grid';
import ReportAnalysisActionsMenuContainer from 'adapter/in/common/containers/Widget/ReportAnalysisActionsMenu/ReportAnalysisActionsMenu';
import { TextBlockActionsMenuContainer } from 'adapter/in/common/containers/Widget/TextBlockActionsMenu/TextBlockActionsMenu';
import { useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';

type WidgetWrapperProps = {
    isEditingLayout: boolean;
    isEditEnabled: boolean;
    hasWidgetDataError: boolean;
    WidgetBuilder: (
        width: number,
        height: number,
        layoutType?: LayoutType,
        editable?: boolean,
        sizeIssue?: ConstraintsPerLayoutElement
    ) => ReactElement;
    dashboardId: string;
    widgetId: string;
    widgetType: WidgetType;
    isSelectedWidget: boolean;
    branding: Branding;
    layoutType: LayoutType;
    newPreset: KeyAndNamePair;
    newFeature: KeyAndNamePair;
    sizeIssue?: ConstraintsPerLayoutElement;
    alertTooltip?: string;
    gridOrigin?: GridOrigin;
    isReportInstance?: boolean;
};

const sizeMeHOC = sizeMe({
    monitorWidth: true,
    monitorHeight: true,
    refreshRate: 300,
});

const WidgetWrapper = (props: WidgetWrapperProps & SizeMeProps): ReactElement => {
    const {
        WidgetBuilder,
        size,
        isEditingLayout,
        isEditEnabled,
        dashboardId,
        widgetId,
        widgetType,
        isSelectedWidget,
        branding,
        layoutType,
        newPreset,
        newFeature,
        hasWidgetDataError,
        sizeIssue,
        alertTooltip,
        gridOrigin = GridOrigin.DASHBOARD,
        isReportInstance,
    } = props;

    const [showMenu, setShowMenu] = useState(false);
    const { editorBeingEdited } = useTextBlockData();

    const renderActionsMenu = () => {
        if (widgetType === WidgetType.ANALYSIS && gridOrigin === GridOrigin.DASHBOARD) {
            return <AnalysisActionsMenuContainer dashboardId={dashboardId} analysisId={widgetId} showMenu={showMenu} />;
        }
        if (widgetType === WidgetType.ANALYSIS && gridOrigin === GridOrigin.REPORT) {
            return <ReportAnalysisActionsMenuContainer dashboardId={dashboardId} analysisId={widgetId} showMenu={showMenu} />;
        }
        if (widgetType === WidgetType.RICH_TEXT) {
            return editorBeingEdited?.textBlockId !== widgetId ? (
                <TextBlockActionsMenuContainer dashboardId={dashboardId} widgetId={widgetId} showMenu={showMenu} />
            ) : null;
        }
        return (
            <WidgetActionsMenuContainer
                showMenu={showMenu}
                showOptions={!hasWidgetDataError}
                newPreset={newPreset}
                newFeature={newFeature}
                dashboardId={dashboardId}
                widgetId={widgetId}
                widgetType={widgetType}
            />
        );
    };

    const dashboardFrame = isDashboardFrame(widgetId);

    return (
        <StyledGrabbale
            data-testid='widget-wrapper'
            data-id={`${dataId}.${gtmIds.widget.notEditable}`}
            $editable={isEditingLayout}
            $isEditEnabled={isEditEnabled}
            $isWidgetSelected={isSelectedWidget}
            $isDashboardFrame={dashboardFrame}
            $branding={branding}
            $gridOrigin={gridOrigin}
            $isEditingLayout={isEditingLayout}
            $isReportInstance={isReportInstance}
            onMouseEnter={() => setShowMenu(true)}
            onMouseLeave={() => setShowMenu(false)}
        >
            {alertTooltip && <SmallAlertTooltip tooltipMessage={alertTooltip} />}
            {isEditEnabled && !isEditingLayout && !dashboardFrame && renderActionsMenu()}
            {WidgetBuilder(size.width!, size.height!, layoutType, isEditingLayout, sizeIssue)}
        </StyledGrabbale>
    );
};

export default sizeMeHOC(WidgetWrapper);
